// AuthProvider + useAuth — FE-A.
//
// Owns the post-login state: the current user, the user's active
// memberships, and the *currently selected* broker entity. Wires
// the selection into the API client so every entity-scoped request
// rides ``X-Entity-Id`` automatically.

import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef,
  useState,
  type ReactNode,
} from "react";

import {
  setAuthLostHandler, setCurrentEntityId,
} from "@/api/client";

import {
  login as apiLogin, logout as apiLogout, me as apiMe,
  refreshSession,
} from "./api";
import type {
  AuthMembership,
  AuthMeResponse,
  AuthUser,
  LoginRequest,
} from "./types";


interface AuthContextValue {
  user: AuthUser | null;
  memberships: AuthMembership[];
  currentMembership: AuthMembership | null;
  currentEntityId: string | null;
  status: "loading" | "authenticated" | "unauthenticated" | "error";
  error: Error | null;
  login: (body: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  switchEntity: (entityId: string) => void;
  refresh: () => Promise<void>;
}


// Exported for test scaffolds (vitest can provide a fake context
// value without standing the full AuthProvider up). Production code
// should always read via ``useAuth``.
export const AuthContext = createContext<AuthContextValue | null>(null);

export type { AuthContextValue };

export const AUTH_QUERY_KEY = ["auth", "me"] as const;

// Proactive refresh cadence (Settings-Sec). Cookie sessions live for
// 14 days but the server re-checks ``user.is_active`` and membership
// status on every refresh — so a tighter cadence is what gives a
// "Suspend" or "Deactivate" admin click teeth. 14 minutes leaves
// generous headroom under the spec's 15-minute access-token target
// without spamming the DB (~100 round-trips per active user per day).
const REFRESH_INTERVAL_MS = 14 * 60 * 1000;


export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [selectedEntityId, setSelectedEntityId] =
    useState<string | null>(null);

  const meQuery = useQuery<AuthMeResponse>({
    queryKey: AUTH_QUERY_KEY,
    queryFn: ({ signal }) => apiMe({ signal }),
    retry: false,
    staleTime: 30_000,
  });

  // Pick a default membership the first time we have data, then
  // keep whatever the user explicitly switched to.
  useEffect(() => {
    if (!meQuery.data) return;
    const memberships = meQuery.data.memberships;
    if (memberships.length === 0) {
      setSelectedEntityId(null);
      setCurrentEntityId(null);
      return;
    }
    setSelectedEntityId((prev) => {
      const stillValid = prev && memberships.some(
        (m) => m.entity.id === prev,
      );
      const next = stillValid ? prev : memberships[0].entity.id;
      setCurrentEntityId(next);
      return next;
    });
  }, [meQuery.data]);

  const memberships = meQuery.data?.memberships ?? [];
  const user = meQuery.data?.user ?? null;
  // Default to the first membership eagerly during render so
  // <PermissionGuard> reads a populated currentMembership on the very
  // first frame after meQuery resolves. The useEffect below persists
  // this choice into selectedEntityId for switchEntity / X-Entity-Id
  // header propagation; until that effect fires, this synchronous
  // fallback prevents a redirect race on fresh URL-bar loads of any
  // permission-gated route.
  const currentMembership = (() => {
    if (memberships.length === 0) return null;
    if (selectedEntityId) {
      const match = memberships.find((m) => m.entity.id === selectedEntityId);
      if (match) return match;
    }
    return memberships[0];
  })();

  const status: AuthContextValue["status"] = (() => {
    if (meQuery.isLoading) return "loading";
    if (meQuery.isError) {
      // 401s come back as ApiError(status=401); treat them as
      // unauthenticated rather than a hard error.
      const status = (meQuery.error as unknown as { status?: number })
        ?.status;
      return status === 401 ? "unauthenticated" : "error";
    }
    if (meQuery.data && user) return "authenticated";
    return "unauthenticated";
  })();

  const login = useCallback(
    async (body: LoginRequest) => {
      await apiLogin(body);
      // Invalidate so the /me query refetches under the new cookie.
      await queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      setSelectedEntityId(null);
      setCurrentEntityId(null);
      queryClient.removeQueries({ queryKey: AUTH_QUERY_KEY });
      // Wipe every cached query — anything that was scoped to the
      // outgoing user must not leak into the next session's view.
      queryClient.clear();
    }
  }, [queryClient]);

  // Auth-lost path: the api client invokes this when /refresh fails
  // (revoked / suspended / deactivated session). Idempotent — repeat
  // calls inside the same tick just no-op the second time. Routing
  // back to /login is delegated to ProtectedRoute, which reads the
  // status flip below.
  const handleAuthLost = useCallback(() => {
    setSelectedEntityId(null);
    setCurrentEntityId(null);
    // Mark /me as a 401 so the provider transitions to
    // unauthenticated immediately. Setting the cached error keeps
    // ProtectedRoute from briefly flashing a "loading" state on the
    // way to /login.
    queryClient.setQueryData(AUTH_QUERY_KEY, undefined);
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => {
    setAuthLostHandler(handleAuthLost);
    return () => setAuthLostHandler(null);
  }, [handleAuthLost]);

  // Proactive session refresh while authenticated. Single-flight
  // dedup lives in the api client, so this interval and any
  // concurrent 401-driven retries collapse onto one round-trip.
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (status !== "authenticated") {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      return;
    }
    refreshIntervalRef.current = setInterval(() => {
      void refreshSession().then((ok) => {
        if (!ok) handleAuthLost();
      });
    }, REFRESH_INTERVAL_MS);
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [status, handleAuthLost]);

  const switchEntity = useCallback((entityId: string) => {
    setSelectedEntityId(entityId);
    setCurrentEntityId(entityId);
    // Invalidate non-auth queries so widgets refetch under the
    // freshly-selected entity.
    queryClient.invalidateQueries({
      predicate: (q) => q.queryKey[0] !== "auth",
    });
  }, [queryClient]);

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      memberships,
      currentMembership,
      currentEntityId: selectedEntityId,
      status,
      error: meQuery.error as Error | null,
      login,
      logout,
      switchEntity,
      refresh,
    }),
    [
      user, memberships, currentMembership, selectedEntityId,
      status, meQuery.error, login, logout, switchEntity, refresh,
    ],
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}


export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
